import dayjs from "dayjs";
import { message } from "antd";
import type {
  DownloadImportExampleReq,
  ExportMaterialsReq,
  FindAllMaterialsReq,
  MaterialApiSource,
  MaterialEntity,
  MaterialImportResultDto,
  MaterialImportValidationErrorDto,
  PagedMaterialListDto,
} from "@/pages/_layout/system/-materialManagement/api-types";
import materialsRequest, { getMaterialsAuthHeaders, materialsHttp } from "@/services/materialsRequest";
import type { ApiResult } from "@/lib/request";
import { _DELETE, _GET } from "@/services/request";

/**
 * 商品素材列表
 * GET /api/materials
 */
export const findAllMaterials = (params?: FindAllMaterialsReq) =>
  materialsRequest<PagedMaterialListDto, FindAllMaterialsReq>(_GET, "/api/materials")(params ?? {});

/**
 * 商品素材详情
 * GET /api/materials/:id
 */
export const findOneMaterial = (id: number) =>
  materialsRequest<MaterialEntity, Record<string, never>>(_GET, `/api/materials/${id}`)({});

/**
 * 删除商品素材
 * DELETE /api/materials/:id
 */
export const deleteMaterial = (id: number) =>
  materialsRequest<null, Record<string, never>>(_DELETE, `/api/materials/${id}`)({});

/**
 * 导入素材（multipart: file + source）
 * POST /api/materials/import
 */
export const importMaterials = async (file: File, source: MaterialApiSource): Promise<MaterialImportResultDto> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("source", source);

  const res = await materialsHttp.post<ApiResult<MaterialImportResultDto>>("/api/materials/import", formData, {
    headers: {
      ...getMaterialsAuthHeaders(),
      "Content-Type": "multipart/form-data",
    },
  });

  const body = res.data;
  if (body.code === 0 && body.data) {
    return body.data;
  }
  const validationErrors = (body.data as unknown as MaterialImportValidationErrorDto | null)?.errors;
  if (validationErrors?.length) {
    return Promise.reject(body);
  }
  message.error(body.msg || "导入失败");
  return Promise.reject(body);
};

function parseContentDispositionFilename(header?: string): string | undefined {
  if (!header) return undefined;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const match = header.match(/filename="?([^";\n]+)"?/i);
  return match?.[1]?.trim();
}

function downloadMaterialsBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * 导出素材（json / xlsx）
 * GET /api/materials/export
 */
export const exportMaterials = async (params: ExportMaterialsReq): Promise<void> => {
  const format = params.format ?? "json";
  const res = await materialsHttp.get<Blob>("/api/materials/export", {
    params: {
      format,
      source: params.source,
    },
    responseType: "blob",
    headers: getMaterialsAuthHeaders(),
  });

  const ext = format === "xlsx" ? "xlsx" : "json";
  const sourceSuffix = params.source ? `_${params.source}` : "";
  const defaultName = `素材列表${sourceSuffix}_${dayjs().format("YYYYMMDD_HHmmss")}.${ext}`;
  const filename = parseContentDispositionFilename(res.headers["content-disposition"]) ?? defaultName;

  downloadMaterialsBlob(res.data, filename);
};

/**
 * 下载导入示例（json / xlsx）
 * GET /api/materials/import/example
 */
export const downloadImportExample = async (params: DownloadImportExampleReq): Promise<void> => {
  const res = await materialsHttp.get<Blob>("/api/materials/import/example", {
    params: {
      source: params.source,
      format: params.format,
    },
    responseType: "blob",
    headers: getMaterialsAuthHeaders(),
  });

  const ext = params.format === "xlsx" ? "xlsx" : "json";
  const defaultName = `素材导入示例_${params.source}.${ext}`;
  const filename = parseContentDispositionFilename(res.headers["content-disposition"]) ?? defaultName;

  downloadMaterialsBlob(res.data, filename);
};

const materialsApis = {
  findAllMaterials,
  findOneMaterial,
  deleteMaterial,
  importMaterials,
  exportMaterials,
  downloadImportExample,
};

export default materialsApis;
